import formatLocaleNumber from "~/helpers/localeNumberFormat";
import i18n from "~/i18n/i18n";

export interface MaterialRequirementBasis {
	containers: number;
	multiple: number;
	required: number;
	unitLabel: null | string | undefined;
}

export interface HardgoodsRequirementBasis extends Omit<MaterialRequirementBasis, "multiple"> {
	eachesPerContainer?: null | number;
}

export interface ContainerBasis {
	container: string;
	plantsPerContainer: number;
}

interface ContainerCategory {
	code?: null | string;
	eaches: number;
	label?: null | string;
}

const MAX_FRACTION_DIGITS = 2;

const MAX_PER_PLANT_FRACTION_DIGITS = 4;

const formatQuantity = (value: number): string =>
	formatLocaleNumber(value, { maximumFractionDigits: MAX_FRACTION_DIGITS });

const trimmedLabel = (unitLabel: MaterialRequirementBasis["unitLabel"]): string => unitLabel?.trim() ?? "";

export const containerBasisFor = (category: ContainerCategory | null | undefined): ContainerBasis | undefined => {
	const container = category?.label?.trim() || category?.code?.trim();
	if (!category || !container || category.eaches <= 0) return undefined;
	return { container, plantsPerContainer: category.eaches };
};

export const containerBasisText = ({ container, plantsPerContainer }: ContainerBasis, multiple = 0): string => {
	if (Number.isNaN(multiple) || multiple <= 0) {
		return i18n.t("materialRequirementBasis.containerBasis", { container, count: plantsPerContainer, ns: "produce" });
	}
	return i18n.t("materialRequirementBasis.containerBasisPerPlant", {
		container,
		count: plantsPerContainer,
		ns: "produce",
		perPlant: formatLocaleNumber(multiple / plantsPerContainer, {
			maximumFractionDigits: MAX_PER_PLANT_FRACTION_DIGITS
		})
	});
};

export const hardgoodsRequirementBasisText = ({
	containers,
	eachesPerContainer,
	required,
	unitLabel
}: HardgoodsRequirementBasis): string | undefined => {
	const label = trimmedLabel(unitLabel);
	if (!label) return undefined;
	if (eachesPerContainer && eachesPerContainer > 0) {
		return i18n.t("materialRequirementBasis.hardgoodsWithEaches", {
			containers: i18n.t("materialRequirementBasis.hardgoodsContainers", {
				containers: formatQuantity(containers),
				count: containers,
				label,
				ns: "produce"
			}),
			ns: "produce",
			plants: i18n.t("materialRequirementBasis.hardgoodsPlantsEach", { count: eachesPerContainer, ns: "produce" }),
			required: formatQuantity(required)
		});
	}
	return i18n.t("materialRequirementBasis.hardgoods", {
		containers: formatQuantity(containers),
		label,
		ns: "produce",
		required: formatQuantity(required)
	});
};

const materialRequirementBasisText = ({
	containers,
	multiple,
	required,
	unitLabel
}: MaterialRequirementBasis): string | undefined => {
	const label = trimmedLabel(unitLabel);
	if (!label) return undefined;
	return i18n.t("materialRequirementBasis.perContainer", {
		containers: formatQuantity(containers),
		label,
		multiple: formatQuantity(multiple),
		ns: "produce",
		required: formatQuantity(required)
	});
};

export default materialRequirementBasisText;
